// Distribution jobs of the Activities page (agg_type activity_distributions): one distribution, all roles or one,
// through dataJobs.ts. The API leaves out of a filtered row whatever the request already says (no geo_type or k5_tier,
// no geo_name on grid cells, no measure or role when exactly one is picked, band_name only with origin_lau,
// band_min/band_max/unit only with ranged bands), so a row takes those from its job. A model-grid area is one job per
// cell size, and its plain ids are keyed again the way the area saves them. Nothing is logged.
import type { CountryCode } from '../config/countries';
import type { DistributionId, DistributionRole } from '../config/products';
import { exportActivities } from './activitiesApi';
import type { ActivitiesJobParams } from './activitiesApi';
import { bodiesPerCellSize, rowAreaKey } from './catalogueRequests';
import { LONG_JOB_TIMING, mapOutcomeRows, runDataJob } from './dataJobs';
import type { JobOutcome, RunJobOptions } from './dataJobs';
import { exportAllRows, runFullDataJob } from './fullResults';
import { nullableNumber, text, unwrap } from './rowValues';

export interface DistributionJobParams {
  agg_type: 'activity_distributions';
  /** The API's `distribution`: exactly one here. */
  distribution: DistributionId;
  /** The API's `role`; left out for all roles. */
  role?: DistributionRole;
  start_time: string;
  end_time: string;
  activity_type: 'daily' | 'hourly';
  geo_type: string;
  geo_ids: Array<number | string>;
  country_code?: CountryCode;
  full: boolean;
}

/** One row of a distribution: the people and arrivals of one band of one area, role and time bucket. */
export interface DistributionRow {
  geo_id: string;
  geo_name: string | null;
  time_bucket: string;
  /** null when the row has no role of its own and the job names none. */
  role: string | null;
  distribution: string;
  /** With origin_lau the origin municipality's id, and band_name its name. */
  band: string;
  band_name: string | null;
  band_min: number | null;
  band_max: number | null;
  unit: string | null;
  /** null when the figure is withheld (below 5 people), never 0. */
  people: number | null;
  arrivals: number | null;
}

const optionalText = (raw: unknown): string | null => (unwrap(raw) == null ? null : text(raw));

/** The body fields a row takes when it leaves them out. */
type RowDefaults = Pick<DistributionJobParams, 'distribution' | 'role' | 'geo_type'>;

/** Normalise one row of a job; null for a row that has no area, time bucket or band and so cannot be placed. */
export function normalizeDistributionRow(raw: unknown, job: RowDefaults): DistributionRow | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = unwrap(r.geo_id);
  const geoId = id == null ? '' : rowAreaKey(id, unwrap(r.geo_type) ?? job.geo_type);
  const timeBucket = text(r.time_bucket);
  const band = text(r.band);
  if (geoId === '' || timeBucket === '' || band === '') return null;
  const name = optionalText(r.geo_name);
  return {
    geo_id: geoId,
    geo_name: name === 'no_name' ? null : name,
    time_bucket: timeBucket,
    role: optionalText(r.role) ?? job.role ?? null,
    distribution: optionalText(r.measure) ?? job.distribution,
    band,
    band_name: optionalText(r.band_name),
    band_min: nullableNumber(r.band_min),
    band_max: nullableNumber(r.band_max),
    unit: optionalText(r.unit),
    people: nullableNumber(r.people),
    arrivals: nullableNumber(r.arrivals),
  };
}

/** The /data-agg bodies of a distribution request: one, or one per cell size for a model-grid area. */
export function distributionRequestBodies(params: DistributionJobParams): DistributionJobParams[] {
  return bodiesPerCellSize(params);
}

/** Submit a distribution job and wait for its preview rows. `truncated` is set when any of its jobs was cut. */
export async function runActivityDistributionsJob(params: DistributionJobParams, o: RunJobOptions = {}): Promise<JobOutcome<DistributionRow>> {
  const outcomes = [];
  // A grid area is asked for once per cell size; the first job is the one the page follows, the rest add rows.
  for (const [i, body] of distributionRequestBodies(params).entries()) {
    const outcome = await runDataJob<unknown>(body, { timing: LONG_JOB_TIMING, ...o, ...(i > 0 ? { onSubmitted: undefined } : {}) });
    outcomes.push(mapOutcomeRows(outcome, (row) => normalizeDistributionRow(row, body)));
  }
  return {
    kind: 'rows',
    jobId: outcomes[0].jobId,
    rows: outcomes.flatMap((outcome) => outcome.rows),
    ...(outcomes.some((outcome) => outcome.truncated) ? { truncated: true as const } : {}),
  };
}

/** Every row of a distribution job, read from its full exports: what a sum needs when the preview was cut. */
export async function fullDistributionRows(params: DistributionJobParams, o: RunJobOptions = {}): Promise<DistributionRow[]> {
  const bodies = distributionRequestBodies({ ...params, full: true });
  const outs = await Promise.all(bodies.map((body) => runFullDataJob(body, { signal: o.signal })));
  return outs.flatMap((out, i) => out.rows.flatMap((raw) => normalizeDistributionRow(raw, bodies[i]) ?? []));
}

/**
 * Export all rows of a distribution job: a full export per cell size, as one table with one header. What the rows
 * leave out comes back as columns of its own, so a file still says what it holds, and the same square number at two
 * cell sizes stays two cells.
 */
export function exportDistributions(params: DistributionJobParams, o: RunJobOptions = {}): Promise<string[][]> {
  const parts = distributionRequestBodies({ ...params, full: true }).map((body) => ({
    body,
    fill: { geo_type: body.geo_type, ...(body.role ? { role: body.role } : {}), measure: body.distribution },
  }));
  return exportAllRows(parts, o);
}

/** What the Activities page submitted: the job on screen, as Export all rows and the way back need it. */
export type ActivitiesRequest =
  | { kind: 'totals'; params: ActivitiesJobParams }
  | { kind: 'distributions'; params: DistributionJobParams };

/** Export all rows of the job on screen. */
export function exportRequest(request: ActivitiesRequest, o: RunJobOptions = {}): Promise<string[][]> {
  return request.kind === 'distributions' ? exportDistributions(request.params, o) : exportActivities(request.params, o);
}
